import classNames from 'classnames'
import { cloneElement, forwardRef, Fragment, isValidElement, Key, ReactNode } from 'react'

import { IconProps, IconSize } from '../icon/types'
import { List, ListItemElement, ListItemProps } from '../list/list'
import { Slot, Slottable } from '../utilities'
import { IconListSize } from './icon-list'
import styles from './icon-list-item.module.scss'
import { useIconList } from './use-icon-list'

export type IconListItemElement = ListItemElement

export interface IconListItemProps extends ListItemProps {
  icon: ReactNode
}

const iconSizeMapping: Record<IconListSize, IconSize> = {
  md: 'sm',
  lg: 'md',
}

const IconListItem = forwardRef<IconListItemElement, IconListItemProps>(function IconListItem(
  props,
  forwardedRef
) {
  const { children, className, icon, asChild, ...rest } = props
  const { size } = useIconList()
  const iconSize = iconSizeMapping[size]

  const Component = asChild ? Slot : Fragment

  const classes = classNames(styles.root, className)

  return (
    <List.Item {...rest} className={classes} ref={forwardedRef} asChild={asChild}>
      <Component>
        <span className={styles.icon} data-fds-iconlist-icon>
          {isValidElement<IconProps>(icon) ? cloneElement(icon, { size: iconSize }) : null}
        </span>
        <Slottable>{renderChildren(props)}</Slottable>
      </Component>
    </List.Item>
  )
})

function renderChildren(props: IconListItemProps): ReactNode {
  const { asChild, children } = props
  if (asChild && isValidElement<{ children?: ReactNode }>(children)) {
    return cloneElement(children, {}, renderText(children.props.children, children.key))
  }
  return renderText(children, null)
}

function renderText(children: ReactNode, key: Key | null): ReactNode {
  return (
    <span className={styles.text} key={key}>
      {children}
    </span>
  )
}

export default IconListItem
